import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { Icon } from '~/components/blocks';
import { OrganizationNotificationFragment } from '~/graphql/generated/graphql';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';
import {
  draftAppointmentPatientProfileDrawerState,
  lineVisitorProfileDrawerState,
  outpatientQuestionnaireEntryProfileDrawerState,
  webVisitorProfileDrawerState,
} from '~/state/reception/atoms';

import { ActionSnackbar } from '../ActionSnackbar';
import { NotificationContentText } from './NotificationContentText';

type Props = {
  notification: OrganizationNotificationFragment;
  onClose: (notificationId: string) => void;
};

export const UberDeliveryNotification = React.memo((props: Props) => {
  const { notification, onClose } = props;
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const setProfileDrawerState = useSetAtom(profileDrawerState);
  const resetOutpatientQuestionnaireEntryProfileDrawerState = useResetAtom(
    outpatientQuestionnaireEntryProfileDrawerState,
  );
  const resetProfileDrawerState = useResetAtom(profileDrawerState);
  const resetLineVisitorProfileDrawerState = useResetAtom(lineVisitorProfileDrawerState);
  const resetWebVisitorProfileDrawerState = useResetAtom(webVisitorProfileDrawerState);
  const resetDraftAppointmentPatientProfileDrawerState = useResetAtom(
    draftAppointmentPatientProfileDrawerState,
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openProfileDrawer = useCallback(() => {
    if (router.pathname !== '/reception') {
      router.push('/reception');
      // unmountが完了する前にページ遷移が完了すると通知が再表示されてしまう
      // 通知を管理してる大元のリストから削除し、通知が再表示されないようにする
      onClose(notification.id);
    } else {
      handleClose();
    }

    const appointmentId = notification.appointment?.id;
    const patientId = notification.appointment?.patient?.id;
    if (!appointmentId) return;
    if (!patientId) return;

    resetOutpatientQuestionnaireEntryProfileDrawerState();
    resetProfileDrawerState();
    resetLineVisitorProfileDrawerState();
    resetWebVisitorProfileDrawerState();
    resetDraftAppointmentPatientProfileDrawerState();
    setProfileDrawerState((state) => ({
      ...state,
      isOpen: true,
      patientId,
      appointmentId,
    }));
  }, [
    router,
    notification.appointment?.patient?.id,
    notification.appointment?.id,
    notification.id,
    onClose,
    handleClose,
    setProfileDrawerState,
    resetOutpatientQuestionnaireEntryProfileDrawerState,
    resetProfileDrawerState,
    resetLineVisitorProfileDrawerState,
    resetWebVisitorProfileDrawerState,
    resetDraftAppointmentPatientProfileDrawerState,
  ]);

  const handleClick = useCallback(() => {
    openProfileDrawer();
  }, [openProfileDrawer]);

  const handleExited = useCallback(() => {
    onClose(notification.id);
  }, [notification.id, onClose]);

  //スナックバーが画面横から出てくるアニメーションを適応させるためuseEffectで通知を開く
  useEffect(() => {
    setIsOpen(true);
  }, []);

  return (
    <ActionSnackbar
      open={isOpen}
      onExited={handleExited}
      isNotification={false}
      notifiedAt={notification.createdAt}
      onClick={handleClick}
      onClose={handleClose}
    >
      <Icon
        color="white"
        icon={
          notification.type === 'time_elapsed_uber_delivery' ||
          notification.type === 'canceled_uber_delivery' ||
          notification.type === 'unfulfilled_uber_delivery' ||
          notification.type === 'reassigned_uber_delivery'
            ? 'uber-alert'
            : 'uber'
        }
        size="xxl"
      />
      <NotificationContentText notification={notification} />
    </ActionSnackbar>
  );
});

UberDeliveryNotification.displayName = 'UberDeliveryNotification';
